// Deps
import { createComponent, FelaComponent } from "react-fela";
import { useState, useEffect, useRef } from "react";
import { gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import Router from "next/router";
import { AnimatePresence, motion } from "framer-motion";
import { colors } from "~/styleguide";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";

// Component
import WordpressLink from "~/components/global/links";
import Icon from "~/components/shared/icon";
import SubnavGroup from "./subnav/group";

// Data
import headerMenu from "~/data/header-menu.json";

export default function MobileNavigation({
    className,
    isOpen,
    children,
    closeNav
}) {
    const { data: menu } = headerMenu;

    const [activeIndex, setActiveIndex] = useState(null);

    const menuRef = useRef(null);

    if (isOpen) {
        disableBodyScroll(menuRef.current);
    }

    /**
     * Listen to document clicks and close the menu if clicking outside
     * of the Subnav panel
     */
    useEffect(() => {
        const onDocumentClick = e => {
            if (
                activeIndex !== null &&
                !e.target.closest(".subnav") &&
                !e.target.closest(".primary-navigation-parent")
            );
        };

        document.addEventListener("click", onDocumentClick, false);

        // Ensure that the menu closes when going to any page
        Router.events.on("routeChangeStart", closeSubnav);

        // Unbind method when component dismounts
        return function cleanup() {
            document.removeEventListener("click", onDocumentClick, false);
            Router.events.off("routeChangeStart", closeSubnav);
        };
    });

    const closeSubnav = () => {
        setActiveIndex(null);
    };

    const closeMobileNav = () => {
        if (closeSubnav) closeSubnav();
        if (closeNav) closeNav();
    };

    /**
     * Determine if the parent menu item has children. If so, render a toggleaable
     * entry that will open/close the subnav. Otherwise, render a simple link.
     *
     * @param {Object} item Wordpress menu entry
     * @param {Integer} index Index of the loop
     */
    const renderTopLevelItem = (item, index) => {
        let isActive = activeIndex === index ? true : false;
        if (item.child_items && item.child_items.length)
            return (
                <HeaderTopLevelElem
                    onClick={() => {
                        // Set the active subnav index, or none, if clicking the same subnav
                        setActiveIndex(activeIndex === index ? null : index);
                    }}
                    extend={headerTopLevelStyles(isActive)}
                >
                    {item.title}{" "}
                    <Icon
                        name="menu-arrow-full"
                        styles={iconStyles(isActive)}
                    />
                </HeaderTopLevelElem>
            );

        return (
            <WordpressLink
                url={item.url}
                label={item.title}
                extend={headerTopLevelStyles(isActive)}
                onClick={closeMobileNav}
            />
        );
    };

    return (
        <AnimatePresence
            onExitComplete={() => {
                if (!isOpen) {
                    clearAllBodyScrollLocks();
                }
            }}
        >
            {isOpen && (
                <motion.div
                    initial={{ x: "-100%" }}
                    animate={{ x: "0" }}
                    exit={{ x: "100%" }}
                    transition={{ ease: "anticipate" }}
                    style={menuWrapStyle}
                    ref={menuRef}
                >
                    <Container isOpen={isOpen} className={className}>
                        {/* Render top-level navigation items */}

                        {menu && menu.items && (
                            <ul className="primary-navigation">
                                {menu.items.map((item, index) => {
                                    return (
                                        <FelaComponent
                                            key={item.ID}
                                            className="primary-navigation-parent"
                                            style={mobileLiStyle}
                                            as="li"
                                        >
                                            {renderTopLevelItem(item, index)}

                                            {activeIndex == index &&
                                                item.child_items &&
                                                item.child_items.map(
                                                    (child, j) => {
                                                        return (
                                                            <SubnavGroup
                                                                heading={
                                                                    child.title
                                                                }
                                                                items={
                                                                    child.child_items
                                                                }
                                                                key={j}
                                                                isMobile={true}
                                                                handleExit={
                                                                    closeMobileNav
                                                                }
                                                            />
                                                        );
                                                    }
                                                )}
                                        </FelaComponent>
                                    );
                                })}
                            </ul>
                        )}

                        {children}
                    </Container>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

const menuWrapStyle = {
    position: "fixed",
    left: 0,
    top: 60,
    width: "100%",
    height: "calc(100% - 60px)",
    overflowY: "scroll",
    overflowX: "hidden",
    overflowScrolling: "touch",
    background: colors.white,
    zIndex: 99
};

const iconStyles = isActive => ({
    fontSize: 7,
    marginLeft: "auto",
    extend: [
        {
            condition: isActive,
            style: {
                transform: "rotate(90deg)"
            }
        }
    ]
});

const headerTopLevelStyles = isActive => ({
    display: "flex",
    alignItems: "center",
    height: "100%",
    fontWeight: "normal",
    color: "#000",
    ":visited": {
        color: "inherit!important"
    },
    extend: [
        {
            condition: isActive,
            style: {
                color: colors.blue,
                fontWeight: "bold"
            }
        }
    ]
});

const mobileLiStyle = {
    display: "inline-block",
    width: "100%",
    height: 50,
    lineHeight: "50px"
};

const Container = createComponent(
    () => ({
        width: "100%",
        height: "100%",
        top: 0,
        zIndex: 0,
        position: "relative",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        "> .primary-navigation": {
            width: "100%",
            cursor: "pointer",
            padding: 0
        },
        "& .mobile-footer-cta": {
            width: "100%",
            marginTop: "auto"
        },
        extend: [fluid("paddingH", gutters.header)]
    }),
    "nav",
    ["className"]
);

const HeaderTopLevelElem = createComponent({}, "p", ["onClick"]);
